import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import FeedbackFab from '../components/feedback/FeedbackFab';
import FeedbackModal from '../components/feedback/FeedbackModal';
import { feedbackApi } from '../services/api';
import { useAuth } from './AuthContext';

export interface RequestFeedbackOptions {
  /** Workflow associado ao feedback. NULL/undefined = feedback geral da plataforma. */
  workflowId?: number | null;
  /**
   * Forca a abertura do modal mesmo que a cadencia ainda nao tenha vencido.
   * Util para a pagina /feedbacks (botao "deixar feedback agora").
   */
  force?: boolean;
  /** Permite ou nao escolher 'Plataforma (geral)'. Padrao: true. */
  allowGeneralFeedback?: boolean;
  /** Permite ou nao trocar o workflow apos abertura. Padrao: !workflowId. */
  allowWorkflowChange?: boolean;
  /** Titulo customizavel do modal. */
  title?: string;
}

interface FeedbackContextType {
  /**
   * Solicita exibir o prompt de feedback ao usuario.
   *
   * - Se `force` for true, abre direto.
   * - Caso contrario, consulta /api/feedbacks/should-prompt e so abre se elegivel.
   * - Retorna `true` se o modal foi aberto, `false` caso contrario.
   */
  requestFeedback: (options?: RequestFeedbackOptions) => Promise<boolean>;
  /** Fecha o modal manualmente, se necessario. */
  closeFeedback: () => void;
  /** Indica se a verificacao de cadencia esta em andamento. */
  isChecking: boolean;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

interface ModalState {
  show: boolean;
  workflowId: number | null;
  allowGeneralFeedback: boolean;
  allowWorkflowChange: boolean;
  title: string | undefined;
}

const INITIAL_STATE: ModalState = {
  show: false,
  workflowId: null,
  allowGeneralFeedback: true,
  allowWorkflowChange: true,
  title: undefined,
};

export const FeedbackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [modalState, setModalState] = useState<ModalState>(INITIAL_STATE);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  const isMountedRef = useRef<boolean>(true);
  const inFlightRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      inFlightRef.current?.abort();
    };
  }, []);

  // Quando o usuario desloga, garante que o modal e a chamada em voo sao encerrados.
  useEffect(() => {
    if (!isAuthenticated) {
      inFlightRef.current?.abort();
      setModalState(INITIAL_STATE);
      setIsChecking(false);
    }
  }, [isAuthenticated]);

  const closeFeedback = useCallback(() => {
    setModalState((prev) => ({ ...prev, show: false }));
  }, []);

  const requestFeedback = useCallback(
    async (options: RequestFeedbackOptions = {}): Promise<boolean> => {
      if (!isAuthenticated) return false;

      const {
        workflowId = null,
        force = false,
        allowGeneralFeedback = true,
        allowWorkflowChange = workflowId === null,
        title,
      } = options;

      // Atalho: se force=true, nao precisa consultar o backend.
      if (force) {
        setModalState({
          show: true,
          workflowId,
          allowGeneralFeedback,
          allowWorkflowChange,
          title,
        });
        return true;
      }

      // Cancela consulta anterior antes de iniciar uma nova.
      inFlightRef.current?.abort();
      const controller = new AbortController();
      inFlightRef.current = controller;

      setIsChecking(true);
      try {
        const result = await feedbackApi.shouldPrompt(workflowId, controller.signal);
        if (!isMountedRef.current) return false;
        if (!result.should_prompt) return false;
        setModalState({
          show: true,
          workflowId,
          allowGeneralFeedback,
          allowWorkflowChange,
          title,
        });
        return true;
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'CanceledError') return false;
        // Falha silenciosa: nao bloqueia o usuario por causa de telemetria de feedback.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[FeedbackContext] should-prompt falhou:', err);
        }
        return false;
      } finally {
        if (isMountedRef.current) setIsChecking(false);
      }
    },
    [isAuthenticated],
  );

  const value = useMemo<FeedbackContextType>(
    () => ({ requestFeedback, closeFeedback, isChecking }),
    [requestFeedback, closeFeedback, isChecking],
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackFab />
      <FeedbackModal
        show={modalState.show}
        onClose={closeFeedback}
        workflowId={modalState.workflowId}
        allowGeneralFeedback={modalState.allowGeneralFeedback}
        allowWorkflowChange={modalState.allowWorkflowChange}
        title={modalState.title ?? 'Como foi sua experiência?'}
      />
    </FeedbackContext.Provider>
  );
};

export const useFeedback = (): FeedbackContextType => {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback deve ser usado dentro de <FeedbackProvider>.');
  }
  return ctx;
};
